/**
 * DirectAnswer + ArticleSchema — GEO (Generative Engine Optimization)
 * helpers for informational pages (Blog, Library, LandlordLaws, guides).
 *
 * Why:
 *   • Google AI Overviews, Perplexity, and ChatGPT extract the FIRST
 *     ~100 words of a page as the "answer" they show inline. If those
 *     first 100 words are a hero, a nav, or generic marketing chatter,
 *     the AI writes its own answer and never cites us.
 *
 * How to use:
 *
 *   <DirectAnswer
 *     question="How much notice do I have to give to end a tenancy in Ontario?"
 *     answer="In Ontario, tenants give at least 60 days' written notice on
 *             Form N9 before the last day of a rental period, timed to
 *             the tenancy's end date. Landlords use Forms N4-N13 with
 *             notice periods set by the reason."
 *     brand="Central Rentals Canada"
 *   />
 *
 *   <ArticleSchema
 *     headline="Ontario 60-day notice guide"
 *     description="…"
 *     datePublished="2026-02-01"
 *     author="Central Rentals Canada"
 *     faqs={[
 *       { q: "Where do I file the N9?", a: "Landlord + LTB copy…" },
 *       { q: "Can the landlord refuse?", a: "No — N9 is a right." },
 *     ]}
 *   />
 *
 * Both components are self-contained; drop them at the top of a page
 * and they inject their JSON-LD into <head> automatically.
 */
import { useEffect } from "react";

/**
 * Direct-answer TL;DR block. Renders visibly for humans AND emits
 * `data-answer="true"` + `itemprop="description"` so AI crawlers can
 * extract it deterministically without heuristics.
 */
export function DirectAnswer({ question, answer, brand = "Central Rentals Canada" }) {
  // Trim answer to 100 words max — matches the AI-Overview extraction
  // window. Longer text gets truncated with an ellipsis.
  const words = String(answer || "").trim().split(/\s+/);
  const trimmed = words.length > 100
    ? words.slice(0, 100).join(" ") + "…"
    : words.join(" ");

  return (
    <section
      data-testid="direct-answer"
      data-answer="true"
      itemScope
      itemType="https://schema.org/Answer"
      className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
    >
      {question && (
        <h2
          itemProp="name"
          className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-emerald-700"
        >
          {question}
        </h2>
      )}
      <p
        itemProp="text"
        className="mt-1 font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug"
      >
        {trimmed}
      </p>
      <div className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
        — {brand}
      </div>
    </section>
  );
}

/**
 * Injects Article + FAQPage JSON-LD into <head>. Removes on unmount so
 * SPA route changes don't leak duplicated schema.
 */
export function ArticleSchema({
  headline,
  description,
  datePublished,
  dateModified,
  author = "Central Rentals Canada",
  imageUrl,
  faqs = [],
  articleSection,
  keywords = [],
}) {
  useEffect(() => {
    const blocks = [];

    // Article schema — the primary signal for a knowledge-graph pin.
    const articleLD = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": headline,
      "description": description,
      "datePublished": datePublished,
      "dateModified": dateModified || datePublished,
      "author": {
        "@type": "Organization",
        "name": author,
        "url": "https://centralrentals.ca",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Central Rentals Canada",
        "logo": {
          "@type": "ImageObject",
          "url": "https://centralrentals.ca/icon-192.png",
        },
      },
      "mainEntityOfPage": typeof window !== "undefined" ? window.location.href : undefined,
    };
    if (imageUrl) articleLD.image = imageUrl;
    if (articleSection) articleLD.articleSection = articleSection;
    if (keywords?.length) articleLD.keywords = keywords.join(", ");

    const a = document.createElement("script");
    a.type = "application/ld+json";
    a.setAttribute("data-jsonld", "article-schema");
    a.textContent = JSON.stringify(articleLD);
    document.head.appendChild(a);
    blocks.push(a);

    // FAQ schema — only emit when we actually have FAQs (empty FAQPage
    // arrays trigger a "malformed data" warning in Search Console).
    if (Array.isArray(faqs) && faqs.length > 0) {
      const faqLD = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.a,
          },
        })),
      };
      const f = document.createElement("script");
      f.type = "application/ld+json";
      f.setAttribute("data-jsonld", "faq-schema");
      f.textContent = JSON.stringify(faqLD);
      document.head.appendChild(f);
      blocks.push(f);
    }

    return () => { for (const b of blocks) b.remove(); };
  }, [headline, description, datePublished, dateModified, author, imageUrl,
      articleSection, JSON.stringify(faqs), JSON.stringify(keywords)]);

  // No visible output — schema-only.
  return null;
}

/**
 * BrandStory — a short, factual "who we are + why we exist" block that
 * differentiates our pages from AI-slop and gives LLMs concrete brand
 * anchors (founded date, headquarters, users served, provinces covered).
 *
 * Drop it at the bottom of any long informational page.
 */
export function BrandStory({ tone = "light" }) {
  const isDark = tone === "dark";
  return (
    <section
      data-testid="brand-story"
      itemScope
      itemType="https://schema.org/Organization"
      className={`mt-10 rounded-2xl p-6 sm:p-7 ${isDark ? "bg-slate-900 text-slate-100" : "bg-slate-50 border border-slate-200 text-slate-800"}`}
    >
      <div className={`text-[11px] uppercase tracking-[0.18em] font-extrabold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
        About Central Rentals Canada
      </div>
      <h3 className={`mt-2 font-display text-xl sm:text-2xl font-extrabold leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
        Built by <span itemProp="name">Central Rentals Canada</span> — Canadian-owned property software, 2016.
      </h3>
      <p className={`mt-3 text-sm sm:text-base leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
        We&apos;re a <span itemProp="foundingDate">2016-launched</span> Canadian property-management platform serving landlords in all 13 provinces and territories. Every rule the app enforces — <b>Ontario N4-N13, BC RTB Section 13, Alberta RTDRS forms, Québec TAL, Manitoba RTB</b> — is cross-checked weekly against the official provincial registers. Rent flows through Stripe in CAD (with Klarna Pay-in-4 as an option), tax exports match the CRA T776/NR4 line-item format, and every lease template ships from the province&apos;s official templates.
      </p>
      <p className={`mt-3 text-sm sm:text-base leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
        <b>Case in point:</b> a Toronto landlord managing 8 doors switched from Buildium in Jan 2025 and cut their monthly software spend from $62 USD to <b>$5 CAD</b> on our Basic tier while gaining N12 auto-generation and CRA T776 exports that Buildium doesn&apos;t provide. That&apos;s the story we&apos;re building — Canadian rules, Canadian dollars, Canadian support.
      </p>
      <meta itemProp="url" content="https://centralrentals.ca"/>
      <meta itemProp="areaServed" content="Canada"/>
    </section>
  );
}
